import { AlertTriangle, Scale } from "lucide-react";

const BAND = {
  below: { label: "Below norm", cls: "text-red-700" },
  within: { label: "Within norm", cls: "text-emerald-700" },
  above: { label: "Above norm", cls: "text-sky-700" },
};

const ordinal = (n) => {
  const s = n % 100 >= 10 && n % 100 <= 20 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th");
  return `${n}${s}`;
};
const kg = (v) => Number(v).toFixed(1);

const Rating = ({ r }) => (
  <span className={`font-medium ${BAND[r.band].cls}`}>
    {BAND[r.band].label} (~{ordinal(r.percentile)} percentile, norm {kg(r.mean)} ± {kg(r.sd)} kg)
  </span>
);

function SarcopeniaFlag({ sar, testId }) {
  if (!sar) return null;
  if (sar.positive) {
    return (
      <div className="flex items-center gap-1.5 rounded border border-red-200 bg-red-50 px-2 py-1 text-xs font-semibold text-red-800" data-testid={testId}>
        <AlertTriangle className="h-3.5 w-3.5" />
        Low grip — sarcopenia screen positive (best {kg(sar.max_grip)} kg &lt; {sar.cutoff} kg, EWGSOP2)
      </div>
    );
  }
  if (!sar.hands_below.length) return null;
  return (
    <div className="text-xs text-amber-800" data-testid={`${testId}-partial`}>
      {sar.hands_below.map((h) => h[0].toUpperCase() + h.slice(1)).join(", ")} hand below the EWGSOP2 cut-off of {sar.cutoff} kg (stronger hand above).
    </div>
  );
}

const sourceNote = (src) => {
  const parts = [...new Set([src?.dob, src?.sex].filter(Boolean))];
  return parts.length ? ` (age & sex from ${parts.join(" / ")})` : "";
};

// Handgrip vs Australian (per hand) + international (best hand) norms, plus EWGSOP2 flag.
export function GripNormsPanel({ cmp, compact = false, testId = "grip-norms" }) {
  if (!cmp) return null;
  if (!cmp.available) {
    return <p className="text-xs text-zinc-500" data-testid={`${testId}-unavailable`}>Norms: {cmp.reason}.</p>;
  }
  const au = cmp.australian;
  const it = cmp.international;
  return (
    <div className={`space-y-1 text-xs text-zinc-700 ${compact ? "" : "rounded-md border border-zinc-200 bg-zinc-50/60 p-3"}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-zinc-500">
        <Scale className="h-3.5 w-3.5" />
        vs norms · {cmp.sex === "male" ? "Male" : "Female"}, age {cmp.age}{sourceNote(cmp.demographic_sources)}
      </div>
      {au ? Object.entries(au.hands).map(([hand, r]) => (
        <div key={hand} data-testid={`${testId}-au-${hand}`}>
          {!compact && <span className="text-zinc-500">Australian ({au.age_band}) · </span>}
          {hand === "right" ? "Right" : "Left"} {kg(r.value)} kg — <Rating r={r} />
        </div>
      )) : <div className="text-zinc-500">Australian norms cover adults 20+ only.</div>}
      <div data-testid={`${testId}-intl`}>
        <span className="text-zinc-500">International · </span>
        best ({it.hand}) {kg(it.value)} kg — <Rating r={it} />
        <span className="text-zinc-500"> · {it.centile_band} centile</span>
      </div>
      <SarcopeniaFlag sar={cmp.sarcopenia} testId={`${testId}-sarcopenia`} />
      {!compact && (
        <div className="text-2xs text-zinc-400">
          {cmp.sources.australian} · {cmp.sources.international} · {cmp.sources.sarcopenia}
        </div>
      )}
    </div>
  );
}
